import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase, Tables } from "../utils/supabase";

export interface NavBadges {
  unreadComms: number;
  urgentOrOverdueTasks: number;
  // Dot indicators (boolean) for other sections
  overdueCalibrations: boolean;
  expiredItems: boolean;
  openComplaints: boolean;
  draftPreparations: boolean;
  documentsReviewDue: boolean;
  expiredTraining: boolean;
}

export function useNavBadges(): NavBadges {
  const { user, selectedPharmacy } = useAuth();
  const [badges, setBadges] = useState<NavBadges>({
    unreadComms: 0,
    urgentOrOverdueTasks: 0,
    overdueCalibrations: false,
    expiredItems: false,
    openComplaints: false,
    draftPreparations: false,
    documentsReviewDue: false,
    expiredTraining: false,
  });

  useEffect(() => {
    if (!selectedPharmacy || !user) return;

    const fetchBadges = async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);

        const [
          commsResult,
          readsResult,
          tasksResult,
          calibrationsResult,
          expiryCompoundingResult,
          expirySpecialtyResult,
          complaintsResult,
          preparationsResult,
          documentsResult,
          trainingResult,
        ] = await Promise.all([
          supabase
            .from(Tables.COMMUNICATIONS)
            .select("id")
            .eq("pharmacy_id", selectedPharmacy.id),
          supabase
            .from(Tables.COMMUNICATION_READS)
            .select("communication_id")
            .eq("user_id", user.id),
          supabase
            .from(Tables.TASKS)
            .select("id, priority, due_date")
            .eq("pharmacy_id", selectedPharmacy.id)
            .in("status", ["pending", "in_progress"]),
          // Overdue calibrations: latest record per scale has a past next_calibration_date
          supabase
            .from(Tables.CALIBRATION_RECORDS)
            .select("id")
            .eq("pharmacy_id", selectedPharmacy.id)
            .lt("next_calibration_date", today)
            .limit(1),
          // Expired/near-expiry compounding materials
          supabase
            .from(Tables.COMPOUNDING_MATERIALS_EXPIRY)
            .select("id")
            .eq("pharmacy_id", selectedPharmacy.id)
            .in("status", ["expired", "near_expiry"])
            .limit(1),
          // Expired/near-expiry pharmaceutical specialties
          supabase
            .from(Tables.PHARMACEUTICAL_SPECIALTIES_EXPIRY)
            .select("id")
            .eq("pharmacy_id", selectedPharmacy.id)
            .in("status", ["expired", "near_expiry"])
            .limit(1),
          // Open or under-investigation complaints
          supabase
            .from(Tables.COMPLAINTS)
            .select("id")
            .eq("pharmacy_id", selectedPharmacy.id)
            .in("status", ["open", "investigating"])
            .limit(1),
          // Preparations still in draft
          supabase
            .from(Tables.PREPARATIONS)
            .select("id")
            .eq("pharmacy_id", selectedPharmacy.id)
            .eq("status", "draft")
            .limit(1),
          // Documents with review date within 30 days or already past
          supabase
            .from("documents")
            .select("id")
            .eq("pharmacy_id", selectedPharmacy.id)
            .lte("review_date", new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10))
            .not("review_date", "is", null)
            .limit(1),
          // Training records that have expired or expire within 30 days
          supabase
            .from("training_records")
            .select("id")
            .eq("pharmacy_id", selectedPharmacy.id)
            .not("expiry_date", "is", null)
            .lte("expiry_date", new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10))
            .limit(1),
        ]);

        const readIds = new Set(
          (readsResult.data || []).map((r) => r.communication_id),
        );
        const unreadComms = (commsResult.data || []).filter(
          (c) => !readIds.has(c.id),
        ).length;

        const now = new Date();
        const urgentOrOverdueTasks = (tasksResult.data || []).filter(
          (t) =>
            t.priority === "urgent" ||
            (t.due_date && new Date(t.due_date) < now),
        ).length;

        setBadges({
          unreadComms,
          urgentOrOverdueTasks,
          overdueCalibrations: (calibrationsResult.data?.length ?? 0) > 0,
          expiredItems:
            (expiryCompoundingResult.data?.length ?? 0) > 0 ||
            (expirySpecialtyResult.data?.length ?? 0) > 0,
          openComplaints: (complaintsResult.data?.length ?? 0) > 0,
          draftPreparations: (preparationsResult.data?.length ?? 0) > 0,
          documentsReviewDue: (documentsResult.data?.length ?? 0) > 0,
          expiredTraining: (trainingResult.data?.length ?? 0) > 0,
        });
      } catch {
        // Silently fail — badges are non-critical UI
      }
    };

    fetchBadges();

    // Refresh every 60 seconds
    const interval = setInterval(fetchBadges, 60_000);
    return () => clearInterval(interval);
  }, [selectedPharmacy?.id, user?.id]);

  return badges;
}
